import { toast } from 'sonner';
import type { MutatorResult } from '@rocicorp/zero';

/**
 * Observes the authoritative server promise for a Zero mutation, keeps the
 * optimistic local write, and restores caller state when the server rejects.
 * Errors surface through the mounted AppToast (Sonner).
 */
export async function runZeroMutation(
  result: Pick<MutatorResult, 'server'> | (() => Pick<MutatorResult, 'server'>),
  options?: {
    errorTitle?: string;
    restore?: () => void;
    quiet?: boolean;
    /** Overrides the toast body (defaults to a generic retry hint). */
    errorDescription?: string;
    /**
     * Localized copy keyed by an exact server error message (stable codes
     * such as `billing/seat-limit`). Wins over `errorDescription` when the
     * server's message matches, in every environment.
     */
    errorDescriptions?: Record<string, string>;
    /**
     * Called with the matched code when `errorDescriptions` hits, also when
     * `quiet` (so a form can show the copy inline instead of a toast).
     */
    onKnownError?: (code: string) => void;
  }
): Promise<boolean> {
  try {
    const outcome = await (typeof result === 'function' ? result() : result)
      .server;
    if (outcome.type === 'error') throw new Error(outcome.error.message);
    return true;
  } catch (error) {
    options?.restore?.();
    const development = process.env.NODE_ENV === 'development';
    if (development) {
      console.error('Zero mutation failed:', error);
    }
    // The server's reason is useful while developing; production keeps the
    // generic copy so ent privacy messages never leak to end users.
    const reason = error instanceof Error ? error.message : undefined;
    const known =
      reason && options?.errorDescriptions
        ? options.errorDescriptions[reason]
        : undefined;
    if (known && reason) options?.onKnownError?.(reason);
    if (!options?.quiet) {
      toast.error(options?.errorTitle ?? 'Could not save', {
        description:
          known ??
          options?.errorDescription ??
          (development && reason ? reason : 'Please try again.')
      });
    }
    return false;
  }
}
